import { Badge, Box, IconButton, Typography, useTheme } from '@mui/material_v7';
import { useRef, useState, useEffect, ReactNode, forwardRef, useImperativeHandle } from 'react';

import ExpandLessIcon from '@uptime/design-system/icons/ExpandLessIcon';
import ExpandMoreIcon from '@uptime/design-system/icons/ExpandMoreIcon';

interface Props {
  header: string;
  badge?: number;
  seamlessFit?: boolean;
  children: ReactNode;
  controls: ReactNode;
}

export interface PageDrawerRef {
  open: () => void;
  close: () => void;
}

// mui_v5 old Page header (with specific height on mobile view); will be replaced with V7 soon
const PAGE_HEADER = '80px';

export const MIN_DRAWER_HEIGHT = '82px';
export const MAX_DRAWER_HEIGHT = '65vh';
export const MAX_DRAWER_HEIGHT_MOBILE = `calc(100vh - ${PAGE_HEADER})`;

const PageDrawer = forwardRef<PageDrawerRef, Props>((props, ref) => {
  const theme = useTheme();
  const drawerRef = useRef<HTMLDivElement>(null);

  const { header, badge, seamlessFit, children, controls } = props;
  const [height, setHeight] = useState(MIN_DRAWER_HEIGHT);
  const [open, setOpen] = useState(Boolean(badge));

  const toggleDrawer = () => setOpen((prev) => !prev);

  useImperativeHandle(
    ref,
    () => ({
      open: () => setOpen(true),
      close: () => setOpen(false),
    }),
    []
  );

  useEffect(() => {
    if (!drawerRef.current) return;

    if (open) {
      const fullHeight = drawerRef.current.scrollHeight;
      setHeight(`${fullHeight}px`);
    } else {
      setHeight(MIN_DRAWER_HEIGHT);
    }
  }, [open, badge]);

  const seamlessFitStyles = seamlessFit ? { mx: -4, mb: -4 } : {};

  return (
    <Box
      ref={drawerRef}
      sx={{
        position: 'sticky',
        bottom: 0,
        mt: 'auto',
        ...seamlessFitStyles,
        pt: 2.5,
        px: 4,
        height,
        transition: 'height 0.5s ease',
        maxHeight: { xs: MAX_DRAWER_HEIGHT_MOBILE, sm: MAX_DRAWER_HEIGHT },
        overflow: 'hidden',
        backgroundColor: theme.palette.grey.white,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: '8px 8px 0px 0px',
        boxShadow: theme.shadows[12],
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h3">
            {header}
            <Badge
              badgeContent={badge}
              sx={{
                ml: 3,
                '.MuiBadge-badge': {
                  mt: '-2px',
                  minWidth: '32px',
                  height: '24px',
                  borderRadius: '112px',
                  backgroundColor: theme.palette.grey[200],
                  fontWeight: 700,
                },
              }}
              showZero
            />
          </Typography>
        </Box>
        <IconButton onClick={toggleDrawer}>
          {open ? <ExpandMoreIcon fontSize="large" /> : <ExpandLessIcon fontSize="large" />}
        </IconButton>
      </Box>
      <Box>{children}</Box>
      <Box>{controls}</Box>
    </Box>
  );
});

export default PageDrawer;
